<script lang="ts" setup>
import type { Ref } from "vue"
import { useMutation, useQuery } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { getCustomTemplate, updateCustomTemplate } from "@/api"
import { EventThemeSelector } from "@/components"
import { Button, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventTemplate } from "@/models"

const props = withDefaults(defineProps<{ templateId?: number }>(), {
  templateId: undefined,
})

const emit = defineEmits<{
  (e: "save"): void
  (e: "success"): void
  (e: "error", err?: unknown): void
  (e: "back"): void
}>()

const { t } = useTranslation()

const updatedTemplate: Ref<Partial<EventTemplate> | undefined> = ref()

const { data: customTemplate, isLoading: isLoadingTemplate } = useQuery({
  queryKey: ["getCustomTemplate", props.templateId],
  queryFn: () => getCustomTemplate(props.templateId),
  enabled: !!props.templateId,
})

const { mutate: updateTemplate, isPending: isUpdatingTemplate } = useMutation({
  mutationFn: (customTemplate: EventTemplate) => updateCustomTemplate(customTemplate),
  onSuccess: async () => {
    emit("success")
  },
  onError: (err) => {
    emit("error", err)
  },
})

const isLoading = computed(() => isLoadingTemplate.value || isUpdatingTemplate.value)

function onSubmit() {
  emit("save")

  const template = new EventTemplate({
    ...customTemplate.value,
    themeJson: updatedTemplate.value?.themeJson,
  })

  updateTemplate(template)
}
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 w-full mb-4">
      <div class="flex flex-col gap-2 mb-4">
        <h1 class="text-xl font-comfortaa font-bold">
          {{ t('template_theme_form.title') }}
        </h1>
        <div class="text-label">
          {{ t('template_theme_form.message') }}
        </div>
      </div>

      <EventThemeSelector
        v-model:theme="updatedTemplate"
        :is-loading="isLoading"
        :custom-template="customTemplate"
      />

      <div class="flex items-center flex-wrap mt-16 gap-2">
        <Button variant="secondary" prefix-icon="ArrowLeft" :disabled="isLoading" @click.prevent="() => emit('back')">
          {{ t('back') }}
        </Button>
        <Button class="w-fit px-10" :disabled="isLoading" @click.prevent="onSubmit">
          {{ t('save_and_continue') }}
          <Icon icon="ArrowRight" />
        </Button>
      </div>
    </div>
  </div>
</template>
